/* eslint @typescript-eslint/no-explicit-any: ['off'] -- "any" types are intentional */

import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DeepPartial, SubmitHandler, UnpackNestedValue, useForm, UseFormMethods } from 'react-hook-form'
import { object, ObjectSchema } from 'yup'
import cleanDeep from 'clean-deep'
import { yupResolver } from '@hookform/resolvers/yup'

import { FormContextProps } from '~shared/components/reactHookForm/FormContext'

import MultiStepFormStepper, { Props as MultiStepFormStepperProps } from './MultiStepFormStepper/MultiStepFormStepper'
import MultiStepFormButtons from './MultiStepFormButtons/MultiStepFormButtons'
import { MultiStepFormContextValues } from './MultiStepFormContext'

/**
 * README
 *
 * useMultiStepForm is based on redux-hook-form(rhf) with yup validation
 * it is used for handling wizards with any number of steps of any complexity
 * the handling of maintaining formdata between steps is outsourced,
 * this way any state management is possible ( little-state-machine, redux, useState ...ect )
 *
 * since at its core it is redux-hook-form(rhf). any inputs which are registered can be used
 * this implementation ( WALIMA - Wandelbots ) uses Material-Ui inputs with Redux for data handling
 *
 * Notes and quirks
 * - the form container will not be unmounted between steps
 * - formValues are used to provide useForm with default values for the active step
 *   since useForm does not get unmounted, this has to be solved with "reset"
 * - since rhf inputs most likely have empty defaults set
 *   we have to remove them with "cleanDeep"
 *   to bypass edge case problems when running the yup validation (resolver)
 *   This part will be more deeply analysed and refactored as it becomes necessary
 *   Please consult Steve about validation Problems! :)
 */

export type OnBack<PossibleFormStepValues> = (formValues: UnpackNestedValue<PossibleFormStepValues>) => void | 'STOP_NAVIGATION'

export interface StepProps<PossibleFormStepValues> {
  component: React.FC
  validationSchema: ObjectSchema<PossibleFormStepValues & Record<string, any>>
  onSubmit: SubmitHandler<any> // using PossibleFormStepValues causes typeproblems where the hook is used
  onBack: OnBack<any> // using PossibleFormStepValues causes typeproblems where the hook is used
  label: string
}

interface UseMultiStepFormProps<FormType, FormSteps extends keyof FormType, PossibleFormStepValues extends FormType[keyof FormType]> {
  currentStep: FormSteps | 'success'
  handleSaveStep: (step: FormSteps, values: PossibleFormStepValues) => void
  handleSetStep: (step: FormSteps) => void
  stepOrder: readonly FormSteps[]
  steps: Record<FormSteps, StepProps<PossibleFormStepValues>>
  successComponent: React.FC
  formValues: Partial<FormType>
  handleSaveForm?: () => void // not yet implemented -> implement if required
  handleResetForm?: () => void
  className?: string
}

export interface MultiStepFormProps {
  multiStepFormContextValues: MultiStepFormContextValues
  formContextProps: FormContextProps
  methods: UseFormMethods<any>
  onSubmitStep: SubmitHandler<any>
  className?: string
}

interface UseMultiStepFormReturn {
  ActiveStepComponent: React.FC
  multiStepFormProps: MultiStepFormProps
  FormStepper: React.FC<MultiStepFormStepperProps>
  FormButtons: React.FC
}

export function useMultiStepForm<
  FormType,
  FormSteps extends keyof FormType = keyof FormType,
  PossibleFormStepValues extends FormType[keyof FormType] = FormType[keyof FormType]
  >(
  props: UseMultiStepFormProps<FormType, FormSteps, PossibleFormStepValues>,
): UseMultiStepFormReturn {
  const { t } = useTranslation()
  const isSuccessStep = props.currentStep === 'success'
  const activeStepIndex = isSuccessStep
    ? props.stepOrder.length
    : props.stepOrder.findIndex(s => s === props.currentStep)

  const successStep: StepProps<Record<never, never>> = {
    component: props.successComponent,
    validationSchema: object({}).defined(),
    onSubmit: () => console.log('but nothing happened'),
    onBack: () => undefined,
    label: t('form:success'),
  }
  const activeStep = props.currentStep === 'success' ? successStep : props.steps[props.currentStep]

  const resolver = yupResolver<PossibleFormStepValues>(activeStep.validationSchema)
  const methods = useForm<PossibleFormStepValues>({
    shouldFocusError: false,
    resolver: (values, context) => {
      const cleanedValues = cleanDeep(values, {
        // notice - if validation shows strange behaviours
        // check cleanDeep does not cause Problems
        emptyArrays: false, // keep empty arrays to trigger errors for them
        emptyObjects: false, // keep empty objects to trigger errors for them
      })
      // @ts-ignore -- cleaned values do not correspond to formValueType since everything can be undefined
      return resolver(cleanedValues, context)
    },
  })

  useEffect(() => {
    const stepValues = props.currentStep !== 'success' ? props.formValues[props.currentStep] : undefined
    // @ts-ignore -- todo solve this type problem
    stepValues && methods.reset(stepValues)

    // eslint-disable-next-line -- only run this when "props.currentStep" changes
  }, [props.currentStep])

  const handleOnClickNextStep = () => {
    if (props.stepOrder.length > activeStepIndex + 1) {
      props.handleSetStep(props.stepOrder[activeStepIndex + 1])
    }
  }

  const handleOnClickPrevStep = () => {
    const stopPrevStepNavigation = activeStep.onBack(methods.getValues())
    if (stopPrevStepNavigation !== 'STOP_NAVIGATION') {
      props.handleSetStep(props.stepOrder[activeStepIndex - 1])
    }
  }

  const handleResetForm = () => {
    methods.reset({} as UnpackNestedValue<DeepPartial<PossibleFormStepValues>>)
    props.handleResetForm && props.handleResetForm()
  }

  const stepLabels = props.stepOrder.map((stepKey) => {
    return props.steps[stepKey].label
  })

  return {
    ActiveStepComponent: activeStep.component,
    multiStepFormProps: {
      onSubmitStep: activeStep.onSubmit,
      formContextProps: {
        schema: activeStep.validationSchema,
      },
      methods: methods,
      multiStepFormContextValues: {
        handleOnClickNextStep,
        handleOnClickPrevStep,
        handleResetForm: props.handleResetForm ? handleResetForm : undefined,
        activeStepIndex: activeStepIndex,
        stepLabels: stepLabels,
      },
      className: props.className,
    },
    FormStepper: MultiStepFormStepper,
    FormButtons: MultiStepFormButtons,
  }
}
